import { useState, useEffect } from "react";
import {X, CheckCircle2, Calendar} from 'lucide-react';
import toast from "react-hot-toast";
import { CONTRACT_LABELS, ContractType, JobCategory, JOB_ACTIVITIES, FRENCH_DEPARTMENTS } from "@/data/jobs";
import { FRENCH_REGIONS } from "@/hooks/useAvailableCandidates";
import { RecruiterJob, NewRecruiterJob } from "@/hooks/useRecruiterJobs";
import { useFormDraft } from "@/hooks/useFormDraft";

// Clé de brouillon local pour une NOUVELLE offre (pas en mode édition).
const JOB_FORM_DRAFT_KEY = "bnssa-mns:draft:job-form";

// ─── Modal création / édition d'offre ────────────────────────────────────────
// Composant autonome réutilisé à la fois dans le back-office recruteur
// (Backoffice.tsx) et dans le hero de la page /offres (Jobs.tsx) pour permettre
// d'initier un dépôt d'annonce avant même la création du compte recruteur.

export interface JobFormModalProps {
  existing: RecruiterJob | null;
  onClose: () => void;
  onSave: (data: NewRecruiterJob) => void;
  /** Libellé personnalisé du bouton de soumission (ex. « Continuer »). */
  submitLabel?: string;
  /**
   * Brouillon de repli (brouillon de pré-inscription déjà soumis) pour
   * réafficher les données si l'utilisateur revient sur la pop-up après être
   * allé sur /inscription sans terminer la création de compte.
   */
  fallbackDraft?: NewRecruiterJob | null;
}

export default function JobFormModal({ existing, onClose, onSave, submitLabel, fallbackDraft }: JobFormModalProps) {
  // Brouillon local actif uniquement pour une nouvelle offre (jamais en édition).
  const { initialDraft, saveDraft, clearDraft } = useFormDraft<{
    data: NewRecruiterJob;
    reqText: string;
    benText: string;
  }>(JOB_FORM_DRAFT_KEY, { enabled: !existing });

  const [data, setData] = useState<NewRecruiterJob>(
    existing
      ? {
          title: existing.title,
          employer: existing.employer,
          location: existing.location,
          department: existing.department,
          region: existing.region || "",
          category: existing.category,
          contractType: existing.contractType,
          salary: existing.salary || "",
          hourlyRate: existing.hourlyRate || "",
          description: existing.description,
          requirements: existing.requirements || [],
          benefits: existing.benefits || [],
          deadline: existing.deadline || "",
          startDate: existing.startDate || "",
          endDate: existing.endDate || "",
          publishedAt: existing.publishedAt,
          urgent: existing.urgent ?? false,
          logementPossible: existing.logementPossible ?? false,
          horairesFixes: existing.horairesFixes ?? false,
          coursParticuliers: existing.coursParticuliers ?? false,
          possibiliteCDI: existing.possibiliteCDI ?? false,
          missionDate: existing.missionDate || "",
          missionStartTime: existing.missionStartTime || "",
          missionEndTime: existing.missionEndTime || "",
          missionHours: existing.missionHours,
          missionPrice: existing.missionPrice,
          activities: existing.activities || [],
        }
      : {
          title: "",
          employer: "",
          location: "",
          department: "",
          region: "",
          category: "BNSSA",
          contractType: "CDI",
          salary: "",
          hourlyRate: "",
          description: "",
          requirements: [],
          benefits: [],
          deadline: "",
          startDate: "",
          endDate: "",
          urgent: false,
          logementPossible: false,
          horairesFixes: false,
          coursParticuliers: false,
          possibiliteCDI: false,
          missionDate: "",
          missionStartTime: "",
          missionEndTime: "",
          missionHours: undefined,
          missionPrice: undefined,
          activities: [],
        }
  );

  // Restauration du brouillon (uniquement en création) une fois au montage.
  const [draftRestored, setDraftRestored] = useState(false);

  // Calcul automatique des heures de mission à partir des horaires
  const computeMissionHours = (start: string, end: string): number | undefined => {
    if (!start || !end) return undefined;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    if (Number.isNaN(sh) || Number.isNaN(sm) || Number.isNaN(eh) || Number.isNaN(em)) return undefined;
    const startMin = sh * 60 + sm;
    let endMin = eh * 60 + em;
    // Si l'heure de fin est avant celle de début, on considère qu'elle déborde sur le jour suivant
    if (endMin <= startMin) endMin += 24 * 60;
    const diff = (endMin - startMin) / 60;
    return Math.round(diff * 100) / 100;
  };

  const isAEorInterim = data.contractType === "auto-entrepreneur" || data.contractType === "interim";
  const isInterim = data.contractType === "interim";
  const isAE = data.contractType === "auto-entrepreneur";
  // Contrats à durée déterminée : on propose une « Date de fin » en plus de la date de début.
  const isFixedTerm = data.contractType === "CDD" || data.contractType === "temps-partiel-CDD";
  const [reqText, setReqText] = useState((existing?.requirements || []).join("\n"));
  const [benText, setBenText] = useState((existing?.benefits || []).join("\n"));

  // Restaure le brouillon sauvegardé (création uniquement) au premier rendu.
  // Priorité : brouillon local `useFormDraft` ; sinon brouillon de pré-inscription
  // (`fallbackDraft`) pour ne jamais afficher un formulaire vide au retour.
  useEffect(() => {
    if (existing || draftRestored) return;
    if (initialDraft && initialDraft.data) {
      setData((prev) => ({ ...prev, ...initialDraft.data }));
      if (typeof initialDraft.reqText === "string") setReqText(initialDraft.reqText);
      if (typeof initialDraft.benText === "string") setBenText(initialDraft.benText);
    } else if (fallbackDraft) {
      setData((prev) => ({ ...prev, ...fallbackDraft }));
      if (Array.isArray(fallbackDraft.requirements)) {
        setReqText(fallbackDraft.requirements.join("\n"));
      }
      if (Array.isArray(fallbackDraft.benefits)) {
        setBenText(fallbackDraft.benefits.join("\n"));
      }
    }
    setDraftRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sauvegarde automatique du brouillon à chaque modification (création uniquement).
  useEffect(() => {
    if (existing || !draftRestored) return;
    saveDraft({ data, reqText, benText });
  }, [existing, draftRestored, saveDraft, data, reqText, benText]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Pour AE / Intérim, la description est facultative (les activités cochées font office de descriptif)
    const requiredBaseValid = isAEorInterim
      ? Boolean(data.title && data.employer && data.location)
      : Boolean(data.title && data.employer && data.location && data.description);
    if (!requiredBaseValid) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    // Validation des champs mission pour AE et Intérim
    if (isAEorInterim) {
      if (!data.missionDate) {
        toast.error("Merci d'indiquer la date de la mission.");
        return;
      }
      if (!data.missionStartTime || !data.missionEndTime) {
        toast.error("Merci d'indiquer les heures de début et de fin de la mission.");
        return;
      }
      if (typeof data.missionHours !== "number" || data.missionHours <= 0) {
        toast.error("Merci d'indiquer le nombre d'heures de la mission.");
        return;
      }
      if (data.category !== "BNSSA" && (!Array.isArray(data.activities) || data.activities.length === 0)) {
        toast.error("Merci de cocher au moins une activité.");
        return;
      }
    }
    if (isInterim) {
      if (typeof data.missionPrice !== "number" || data.missionPrice <= 0) {
        toast.error("Merci d'indiquer le prix de la mission (€).");
        return;
      }
    }
    onSave({
      ...data,
      // Pour AE/Intérim : pas de profil/avantages, on les vide explicitement.
      // Pour les autres contrats : on parse les listes textuelles classiques.
      requirements: isAEorInterim ? [] : reqText.split("\n").filter(Boolean),
      benefits: isAEorInterim ? [] : benText.split("\n").filter(Boolean),
      activities: isAEorInterim && data.category !== "BNSSA" ? (data.activities || []) : [],
    });
    // Envoi réussi → on nettoie le brouillon local (création uniquement).
    if (!existing) clearDraft();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {existing ? "Modifier l'offre" : "Nouvelle offre"}
          </h2>
          <button onClick={onClose} aria-label="Fermer" className="p-1.5 hover:bg-gray-100 rounded-md">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Titre du poste *</label>
            <input
              type="text"
              required
              value={data.title}
              onChange={(e) => setData({ ...data, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{isAE ? "Établissement *" : "Employeur / Établissement *"}</label>
            <input
              type="text"
              required
              value={data.employer}
              onChange={(e) => setData({ ...data, employer: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Ville *</label>
              <input
                type="text"
                required
                value={data.location}
                onChange={(e) => setData({ ...data, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Département</label>
              <select
                value={data.department}
                onChange={(e) => setData({ ...data, department: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              >
                <option value="">— Sélectionner un département —</option>
                {FRENCH_DEPARTMENTS.map((dep) => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Région <span className="text-gray-500 font-normal">(utilisée pour les alertes emploi)</span>
            </label>
            <select
              value={data.region || ""}
              onChange={(e) => setData({ ...data, region: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
            >
              <option value="">— Sélectionner une région —</option>
              {FRENCH_REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              Permet de notifier automatiquement les candidats ayant créé une alerte sur cette région.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Catégorie</label>
              <select
                value={data.category}
                onChange={(e) => setData({ ...data, category: e.target.value as JobCategory })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              >
                <option value="BNSSA">BNSSA</option>
                <option value="MNS">MNS</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Type de contrat</label>
              <select
                value={data.contractType}
                onChange={(e) => setData({ ...data, contractType: e.target.value as ContractType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              >
                {(Object.keys(CONTRACT_LABELS) as ContractType[]).map((c) => (
                  <option key={c} value={c}>{CONTRACT_LABELS[c]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!isAEorInterim && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Salaire / Rémunération</label>
                <input
                  type="text"
                  value={data.salary || ""}
                  onChange={(e) => setData({ ...data, salary: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Ex: 2 000 € - 2 500 € brut/mois"
                />
              </div>
            )}
            {isAE && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tarif horaire indicatif (auto-entrepreneur)</label>
                <input
                  type="text"
                  value={data.hourlyRate || ""}
                  onChange={(e) => setData({ ...data, hourlyRate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Ex: 18 € - 25 €/heure"
                />
              </div>
            )}
          </div>

          {/* Date de début souhaitée — tous contrats sauf AE & Intérim (mission ponctuelle à la place) */}
          {/* Pour CDD & temps partiel CDD, on ajoute aussi une « Date de fin » à côté. */}
          {!isAEorInterim && (
            <div className={isFixedTerm ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : undefined}>
              <div>
                <label htmlFor="startDate" className="block text-xs font-semibold text-gray-700 mb-1">
                  Date de début souhaitée <span className="text-gray-500 font-normal">(facultatif)</span>
                </label>
                <input
                  id="startDate"
                  type="date"
                  value={data.startDate || ""}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setData({ ...data, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Date à laquelle vous souhaitez que le candidat prenne son poste.
                </p>
              </div>
              {isFixedTerm && (
                <div>
                  <label htmlFor="endDate" className="block text-xs font-semibold text-gray-700 mb-1">
                    Date de fin <span className="text-gray-500 font-normal">(facultatif)</span>
                  </label>
                  <input
                    id="endDate"
                    type="date"
                    value={data.endDate || ""}
                    min={data.startDate || new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setData({ ...data, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Date de fin du contrat à durée déterminée.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Bloc Mission unique — uniquement pour AE et Intérim */}
          {isAEorInterim && (
            <div className={`border rounded-xl p-4 space-y-3 ${
              isInterim
                ? "border-purple-200 bg-purple-50/40"
                : "border-amber-200 bg-amber-50/40"
            }`}>
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold text-gray-900">
                    Détails de la mission {isInterim ? "(intérim)" : "(auto-entrepreneur)"}
                  </p>
                  <p className="text-[11px] text-gray-600 mt-0.5">
                    Sélectionnez le jour exact, l'amplitude horaire{isInterim ? " et le prix total payé pour cette mission" : " et le nombre d'heures"}. Une mission = un seul jour.
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="missionDate" className="block text-xs font-semibold text-gray-700 mb-1">
                  Jour de la mission <span className="text-red-500">*</span>
                </label>
                <input
                  id="missionDate"
                  type="date"
                  required={isAEorInterim}
                  value={data.missionDate || ""}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setData({ ...data, missionDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="missionStart" className="block text-xs font-semibold text-gray-700 mb-1">
                    Heure de début <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="missionStart"
                    type="time"
                    required={isAEorInterim}
                    value={data.missionStartTime || ""}
                    onChange={(e) => {
                      const start = e.target.value;
                      const auto = computeMissionHours(start, data.missionEndTime || "");
                      setData({
                        ...data,
                        missionStartTime: start,
                        missionHours: auto ?? data.missionHours,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  />
                </div>
                <div>
                  <label htmlFor="missionEnd" className="block text-xs font-semibold text-gray-700 mb-1">
                    Heure de fin <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="missionEnd"
                    type="time"
                    required={isAEorInterim}
                    value={data.missionEndTime || ""}
                    onChange={(e) => {
                      const end = e.target.value;
                      const auto = computeMissionHours(data.missionStartTime || "", end);
                      setData({
                        ...data,
                        missionEndTime: end,
                        missionHours: auto ?? data.missionHours,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="missionHours" className="block text-xs font-semibold text-gray-700 mb-1">
                    Nombre d'heures <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="missionHours"
                      type="number"
                      min="0"
                      step="0.25"
                      required={isAEorInterim}
                      value={typeof data.missionHours === "number" ? data.missionHours : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setData({
                          ...data,
                          missionHours: v === "" ? undefined : parseFloat(v),
                        });
                      }}
                      className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                      placeholder="Ex: 8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">h</span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">Calculé automatiquement à partir des horaires, ajustable.</p>
                </div>

                {isInterim && (
                  <div>
                    <label htmlFor="missionPrice" className="block text-xs font-semibold text-gray-700 mb-1">
                      Prix payé pour la mission <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="missionPrice"
                        type="number"
                        min="0"
                        step="1"
                        required={isInterim}
                        value={typeof data.missionPrice === "number" ? data.missionPrice : ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setData({
                            ...data,
                            missionPrice: v === "" ? undefined : parseFloat(v),
                          });
                        }}
                        className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                        placeholder="Ex: 180"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">€</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">Prix total payé au candidat pour cette mission (et non un salaire mensuel).</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Activités cochées — uniquement pour AE et Intérim, sauf BNSSA */}
          {isAEorInterim && data.category !== "BNSSA" && (
            <div className="border border-blue-200 bg-blue-50/40 rounded-xl p-4">
              <p className="text-xs font-bold text-gray-900 mb-1">
                Activités de la mission <span className="text-red-500">*</span>
              </p>
              <p className="text-[11px] text-gray-600 mb-3">
                Cochez les activités concernées par cette mission (au moins une).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {JOB_ACTIVITIES.map((act) => {
                  const checked = (data.activities || []).includes(act);
                  return (
                    <label
                      key={act}
                      className={`flex items-center gap-2.5 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                        checked
                          ? "bg-white border-blue-400 ring-1 ring-blue-200"
                          : "bg-white border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set<string>(data.activities || []);
                          if (e.target.checked) next.add(act);
                          else next.delete(act);
                          setData({ ...data, activities: Array.from(next) });
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-800 font-medium">{act}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Description {isAEorInterim ? <span className="text-gray-500 font-normal">(facultatif)</span> : <span className="text-red-500">*</span>}
            </label>
            <textarea
              required={!isAEorInterim}
              rows={4}
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              placeholder={isAEorInterim ? "Précisez éventuellement quelques détails utiles (lieu précis, public, matériel…)." : undefined}
            />
          </div>
          {!isAEorInterim && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Profil recherché <span className="text-gray-500 font-normal">(une ligne par exigence)</span>
              </label>
              <textarea
                rows={3}
                value={reqText}
                onChange={(e) => setReqText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              />
            </div>
          )}
          {!isAEorInterim && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Avantages <span className="text-gray-500 font-normal">(une ligne par avantage)</span>
              </label>
              <textarea
                rows={3}
                value={benText}
                onChange={(e) => setBenText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              />
            </div>
          )}

          {/* Options supplémentaires */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <p className="text-xs font-bold text-gray-700 mb-2">Options de l'offre</p>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={data.urgent ?? false}
                onChange={(e) => setData({ ...data, urgent: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm text-gray-700 font-medium">⚡ Urgent</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={data.logementPossible ?? false}
                onChange={(e) => setData({ ...data, logementPossible: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700 font-medium">🏠 Logement possible</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={data.horairesFixes ?? false}
                onChange={(e) => setData({ ...data, horairesFixes: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-gray-600 focus:ring-gray-500"
              />
              <span className="text-sm text-gray-700 font-medium">🕒 Horaires fixes</span>
            </label>
            {data.category === "MNS" && (
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.coursParticuliers ?? false}
                  onChange={(e) => setData({ ...data, coursParticuliers: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 font-medium">🎓 Cours particuliers 100%</span>
              </label>
            )}
            {(data.contractType === "CDD" || data.contractType === "temps-partiel-CDD") && (
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.possibiliteCDI ?? false}
                  onChange={(e) => setData({ ...data, possibiliteCDI: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-gray-700 font-medium">📈 Possibilité de CDI</span>
              </label>
            )}
          </div>

          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-semibold text-sm text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-gray-900 hover:bg-black text-white px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              {submitLabel ? submitLabel : existing ? "Mettre à jour" : "Créer l'offre"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
